/* ============================================================
   custom.pdp.title-fit.js — solves the product title's FONT SIZE so the
   words fill their tag exactly. Rewritten 12 Aug 2026 from the 31 Jul
   original; this version owns nothing but the size.

   Devin: "i want the soap title to fit perfectly in the width… it should be
   a slightly larger font size to make it fill up the width."

   WHY THIS CANNOT BE CSS. A container query unit (the trick the product
   cards use — font-size: 6.75cqw) scales type with the CONTAINER, which is
   the same for every product. Filling the width means scaling with the TEXT
   too: "LOOPY LAVENDER" and "FILTHY FARMGIRL TEE — FULL LOGO" need very
   different sizes to occupy the same span. Only measurement can tell them
   apart. That irreducible measurement is this file's ONLY job.

   THE CONTRACT WITH THE STYLESHEET (the geometry note on .ffg-pdp__title in
   custom.pdp.css is the other half):
     · CSS owns the box. width:auto against the column, cap runway as a
       transparent em border, the 5px bar overhang as a margin. The box's
       width does not depend on font size — margins are px, the border and
       padding are em and belong to the CHROME the solve accounts for.
     · This file writes exactly two things on the element: the custom
       property --ffg-pdp-title-size (read by the stylesheet's font-size
       rule) and data-ffg-fit="line"|"wrap" (state; the stylesheet decides
       that "line" means nowrap). Never a width, never a margin, never an
       inline !important. If this script fails to load, the title renders
       at the stylesheet's 34px fallback and wraps — a smaller headline,
       not a broken page.
     · ≥750px only. Below that a later rule pins the size to a fixed 34px
       and the tag spans its column — there is nothing to solve, so the
       fitter stands down and clears its two outputs.

   ONE MEASUREMENT, NO LOOP. Every length inside the tag is em (border,
   padding, tracking), so rendered width is LINEAR in font size, and one
   probe at a reference size answers outright:

       text(size) + chrome(size) = W
       size/REF * refWidth + size * chromeRatio = W
       size = W / (refWidth/REF + chromeRatio)

   The only search left is the multi-line case: when one line would need
   less than MIN, the largest size that still fits MAX_LINES is found by
   bisection on the live line count.
   ============================================================ */
(function () {
  'use strict';

  var TITLE = '.ffg-pdp__title';
  var VAR = '--ffg-pdp-title-size';
  var FIT = 'ffgFit'; /* dataset key for data-ffg-fit */
  var REF = 100;
  /* The floor at which a single line is still worth having; below it the
     title wraps and the bisection takes over. 24, not 30 — at 30 the whole
     751–790px band fell through to the multi-line branch (31 Jul). */
  var MIN = 24;
  /* Sanity ceiling, not a design choice. 120, raised from 72 — at 72 a short
     name in a wide column stopped with the tag half empty (31 Jul). */
  var MAX = 120;
  /* TWO. Three lines only happens in a narrow column, where three lines of
     fitted display type is a wall, not a headline (Devin, 31 Jul). */
  var MAX_LINES = 2;

  var wideMQ = window.matchMedia('(min-width: 750px)');

  /* The REGIME comes from CSS, not from the mq above (12 Aug, the
     Firefox-sidebar bug: at fractional zoomed widths matchMedia and the
     CSS engine can disagree by a rounding hair). FFG.isWide() reads the
     --ffg-regime sentinel the stylesheet itself sets; wideMQ stays only
     as a fallback oracle and as a cheap change TRIGGER — a trigger may
     fire spuriously, the decision it triggers may not. */
  function isWide() {
    return window.FFG && window.FFG.isWide ? window.FFG.isWide() : wideMQ.matches;
  }

  function fit(el) {
    var cs = getComputedStyle(el);
    var fs = parseFloat(cs.fontSize) || 1;

    /* The chrome — cap border and text padding — is em, so as a ratio of
       the current size it is a constant of the design, whatever size this
       solve lands on. */
    var chromeRatio =
      ((parseFloat(cs.borderLeftWidth) || 0) +
        (parseFloat(cs.borderRightWidth) || 0) +
        (parseFloat(cs.paddingLeft) || 0) +
        (parseFloat(cs.paddingRight) || 0)) /
      fs;

    /* The width to fill is the element's own border box — CSS put it there
       (column width plus the 5px overhangs) and nothing about it depends on
       the font, so there is no neutral-state reset and no feedback loop. */
    var W = el.getBoundingClientRect().width;
    if (!(W > 0)) return;

    /* Measure on a clone, off-screen, so the live element never flashes
       through a reference size. The reference is set through the custom
       property because the stylesheet's font-size rule is !important — an
       inline font-size would lose to it and measure nothing (31 Jul). The
       clone's own border/padding are stripped: text width is what's wanted,
       chrome is already accounted for above. */
    var probe = el.cloneNode(true);
    probe.removeAttribute('style');
    probe.style.cssText =
      'position:absolute;visibility:hidden;white-space:nowrap;width:auto;' +
      'max-width:none;margin:0;padding:0;border:none;left:-9999px;top:0;';
    probe.style.setProperty(VAR, REF + 'px');
    document.body.appendChild(probe);
    var refWidth = probe.getBoundingClientRect().width;

    /* The widest single word, same probe: in the wrap branch it caps the
       size at "the longest word exactly fills one line", because past that
       the browser breaks mid-word — "CHOCOLA / TE COCK / SOAP" (31 Jul). */
    var words = (el.textContent || '').trim().split(/\s+/);
    var widestWord = 0;
    for (var i = 0; i < words.length; i++) {
      probe.textContent = words[i];
      var w = probe.getBoundingClientRect().width;
      if (w > widestWord) widestWord = w;
    }
    document.body.removeChild(probe);
    if (!(refWidth > 0)) return;

    /* 0.5% under the exact solve: subpixel rounding must never leave the
       text a hair wider than its box. Under nowrap an overshoot only pokes
       invisibly into the transparent cap runway, but half a percent is
       cheaper than reasoning about which browser rounds which way. (The
       old version instead re-checked scrollWidth afterwards — a check the
       caps' own overhang made permanently true, so it silently demoted
       every title to white-space:normal and the nowrap arm never ran.) */
    var oneLine = (W / (refWidth / REF + chromeRatio)) * 0.995;
    var size = Math.min(MAX, oneLine);
    var mode = 'line';

    if (oneLine < MIN) {
      /* Fill MAX_LINES instead: with wrapping on, size only changes how
         many lines the text takes, so the largest size that still fits is
         a clean bisection on the live line count. Ten passes lands inside
         a pixel. */
      mode = 'wrap';
      el.dataset[FIT] = 'wrap';
      var lo = MIN;
      var wordCap = widestWord > 0 ? W / (widestWord / REF + chromeRatio) : MAX;
      var hi = Math.min(MAX, oneLine * MAX_LINES * 1.4, wordCap);
      if (hi < lo) lo = hi;
      for (var j = 0; j < 10; j++) {
        var mid = (lo + hi) / 2;
        el.style.setProperty(VAR, mid + 'px');
        var lh = parseFloat(getComputedStyle(el).lineHeight) || mid * 1.05;
        if (Math.round(el.clientHeight / lh) <= MAX_LINES) lo = mid;
        else hi = mid;
      }
      size = lo;
    }

    el.style.setProperty(VAR, size.toFixed(2) + 'px');
    el.dataset[FIT] = mode;
  }

  function standDown(el) {
    el.style.removeProperty(VAR);
    delete el.dataset[FIT];
  }

  /* QUEUED, NOT SWALLOWED (13 Aug): during a live resize notifications
     arrive mid-fit; dropping them let a stale solve be the last word. A
     dropped notification marks pending and the release re-runs once, so
     the final geometry always gets the final say. Released on the next
     frame, because the style write above schedules layout whose
     notifications arrive after this stack unwinds. */
  var busy = false;
  var pendingFit = false;
  function fitAll() {
    if (busy) {
      pendingFit = true;
      return;
    }
    busy = true;
    try {
      var titles = document.querySelectorAll(TITLE);
      Array.prototype.forEach.call(titles, isWide() ? fit : standDown);
    } finally {
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          busy = false;
          if (pendingFit) {
            pendingFit = false;
            fitAll();
          }
        });
      } else {
        busy = false;
      }
    }
  }

  /* Module-level so a re-init (per navigation swap, per editor re-render)
     replaces observers bound to dead markup instead of stacking new ones.
     The window listeners bind once — the window survives everything and
     fitAll re-queries the DOM. */
  var ro = null;
  var mo = null;
  var windowBound = false;

  function init() {
    fitAll();

    /* Antiquary Wide is self-hosted; measuring before it lands measures the
       fallback face and the answer is wrong by the difference. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);

    if (!windowBound) {
      windowBound = true;
      window.addEventListener('resize', fitAll);
      wideMQ.addEventListener('change', fitAll);
    }

    /* The title's parent is the column: its width is the one input the
       solve has, so it is the one box worth observing. (Height echoes from
       the fit's own size write are coalesced by the busy/pending pair.) */
    if (window.ResizeObserver) {
      if (ro) ro.disconnect();
      ro = new ResizeObserver(fitAll);
      Array.prototype.forEach.call(document.querySelectorAll(TITLE), function (el) {
        if (el.parentElement) ro.observe(el.parentElement);
      });
    }

    /* Horizon re-renders the product form on variant change; the new title
       arrives unfitted. The observer only ever schedules fitAll, and the
       probe lives on <body>, outside this subtree — it cannot loop. */
    if (window.MutationObserver) {
      var scope = document.querySelector('[id^="ProductInformation-"]');
      var host = (scope && scope.closest('[id^="shopify-section-"]')) || document.body;
      if (mo) mo.disconnect();
      mo = new MutationObserver(fitAll);
      mo.observe(host, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  /* No-reload navigation replaces the section without a new document —
     everything observed above detaches on the swap; re-arm per swap. */
  document.addEventListener('ffg:navigated', init);
})();
